/**
 * Drop-in replacement for RTK Query's `fetchBaseQuery` that adds
 * FlatBuffer binary serialization for API requests and responses.
 *
 * Content negotiation:
 * - Sends `Accept: application/flatbuffers` on every request that does not
 *   set its own Accept header.
 * - For mutations with a body: reads `extraOptions.fbSerializer`, the
 *   serializer the endpoint declared for itself. If it names one, the body is
 *   packed as FlatBuffer with LN2E envelope wrapping and
 *   `Content-Type: application/flatbuffers`. Endpoints that name none send
 *   JSON. Nothing about the request URL takes part in this decision — see
 *   `requestSerializers.ts`.
 * - For responses: if the response is an LN2E envelope, extracts the
 *   schema hash, looks up the handler in the schema registry, verifies
 *   and unpacks. Non-enveloped OK responses emit a warning and fall back
 *   to JSON parsing as a transitional safety net during migration.
 * - A response whose `X-LN2-Schema` names an API schema other than this
 *   bundle's is decoded only when the schema API's verdict for its type says
 *   this bundle reads it unchanged; otherwise the result is a CUSTOM_ERROR that
 *   says why (`apiVerdicts.ts`).
 *
 * Usage in an API file — through `createRetryingBaseQuery`
 * (`redux/retryingBaseQuery.ts`), which wraps this base query in the app's
 * retry rule rather than each API calling `retry` itself:
 * ```ts
 * import { createRetryingBaseQuery } from "redux/retryingBaseQuery";
 *
 * const baseQuery = createRetryingBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "");
 *
 * // …and on an endpoint that should send FlatBuffer rather than JSON:
 * putBank: builder.mutation({
 *   query: (body) => ({ url: `/banks/${body.id}`, method: "PUT", body: body.body }),
 *   extraOptions: { fbSerializer: "UpdateBank" },
 * }),
 * ```
 */

import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query";
import { extractEnvelopeWithHash } from "../envelope";
import { responseSchemaRefusal } from "./apiVerdicts";
import { getSchemaHandler } from "./schemaRegistry";
import { noteServerSchema, responseSchema, schemaRequestHeaders } from "./serverSchemas";
import { logError } from "../../utils/devLog";
import { serializeRequestBody, type SerializerName } from "./requestSerializers";
import {
  emitEndpointEvent,
  hasEndpointListeners,
  nextEndpointEventId,
} from "./endpointInterceptor";

/**
 * Per-endpoint options this base query understands.
 *
 * RTK Query passes an endpoint's `extraOptions` straight through to its base
 * query, so this is the endpoint's own declaration of how its body travels.
 * Because the type flows into `builder.mutation`, a name that is not a
 * `SerializerName` fails to compile.
 */
export interface FlatBufferExtraOptions {
  /**
   * Name of the request serializer to pack this endpoint's body with.
   * Omit it to send the body as JSON.
   */
  fbSerializer?: SerializerName;
}

/**
 * `data.reason` of the CUSTOM_ERROR returned for a schema refusal: a 2xx reply
 * that arrived in full, whose type the server's schema changed in a way this
 * app cannot read. See `isSchemaRefusal`.
 */
export const SCHEMA_REFUSAL = "schema-refusal";

/**
 * Whether `error` is a schema refusal. The verdict behind one is cached, so
 * sending the request again is refused the same way; retry rules treat it as
 * final (see `isUnreadableReply` in redux/retryConditions.ts).
 */
export function isSchemaRefusal(error: FetchBaseQueryError): boolean {
  if (error.status !== "CUSTOM_ERROR" || typeof error.data !== "object" || error.data === null) {
    return false;
  }
  return (error.data as { reason?: unknown }).reason === SCHEMA_REFUSAL;
}

/**
 * Creates an RTK Query base query function with FlatBuffer binary support.
 *
 * Outbound requests whose endpoint declared `fbSerializer` are FlatBuffer-encoded.
 * Inbound responses with LN2E envelopes are automatically unpacked.
 * Non-enveloped OK responses log a warning and fall back to JSON parsing
 * as a transitional safety net while controllers complete migration.
 *
 * @param baseUrl  The API base URL (e.g. `process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE`).
 */
function createFlatBufferBaseQuery(
  baseUrl: string,
): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  FlatBufferExtraOptions,
  FetchBaseQueryMeta
> {
  const jsonBaseQuery = fetchBaseQuery({ baseUrl });

  return async (args, api, extraOptions) => {
    // Normalize args to FetchArgs object.
    const fetchArgs: FetchArgs = typeof args === "string" ? { url: args } : { ...args };

    // Ensure headers object exists.
    const headers: Record<string, string> =
      typeof fetchArgs.headers === "object" && fetchArgs.headers !== null
        ? { ...(fetchArgs.headers as Record<string, string>) }
        : {};

    // Signal the server we accept FlatBuffer responses, unless the endpoint
    // asked for something else. An endpoint whose response carries fields no
    // FlatBuffer table has (the one-time secret of POST /externals/, say)
    // sends its own Accept: application/json and gets LiftCloud's JSON.
    const explicitAccept = Object.keys(headers).find((name) => name.toLowerCase() === "accept");
    if (explicitAccept === undefined) {
      headers.Accept = "application/flatbuffers";
    }

    // Name the API schema this bundle was built with, so the server can tell a
    // client built with another; it reads the request the same either way. Only
    // a server that has named its own schema is sent the header: an older one's
    // CORS preflight would refuse the request for it.
    Object.assign(headers, schemaRequestHeaders(baseUrl));

    // Serialize the request body with the serializer this endpoint declared.
    // Canonicalise the method that goes on the wire, not just the local copy
    // used for the checks below. The Fetch spec uppercases only DELETE, GET,
    // HEAD, OPTIONS, POST and PUT; any other method is sent as written, and
    // nginx rejects a lowercase "patch" with a 400.
    const method = (fetchArgs.method ?? "GET").toUpperCase();
    fetchArgs.method = method;
    const originalBody = fetchArgs.body;
    const fbSerializer = extraOptions?.fbSerializer;
    if (
      fbSerializer &&
      fetchArgs.body &&
      !(fetchArgs.body instanceof FormData) &&
      (method === "POST" || method === "PUT" || method === "PATCH")
    ) {
      try {
        const serialized = serializeRequestBody(fbSerializer, fetchArgs.body);
        if (serialized) {
          fetchArgs.body = serialized;
          headers["Content-Type"] = "application/flatbuffers";
        }
      } catch (packError) {
        logError(`[FlatBuffer] Failed to pack ${method} ${fetchArgs.url}:`, packError);
        return {
          error: {
            status: "CUSTOM_ERROR",
            error: String(packError),
            data: `FlatBuffer serialization failed: ${packError}`,
          } as FetchBaseQueryError,
        };
      }
    }

    fetchArgs.headers = headers;

    // Interceptor capture state — only allocated when listeners exist.
    const intercepting = hasEndpointListeners();
    const startTime = intercepting ? performance.now() : 0;
    let capturedSchemaHash: string | null = null;
    let capturedTypeName: string | null = null;
    let capturedIsFlatBuffer = false;
    let capturedResponseBody: unknown = null;
    let capturedError: string | null = null;

    // Why a response written with another API schema was not decoded: set
    // instead of throwing, so the result says it in words rather than as a
    // bare PARSING_ERROR.
    let schemaRefusal: string | null = null;
    const authorizationName = Object.keys(headers).find(
      (name) => name.toLowerCase() === "authorization",
    );
    const authorization = authorizationName === undefined ? undefined : headers[authorizationName];

    // If the endpoint supplied its own responseHandler (e.g. blob downloads),
    // respect it — do not overwrite with FlatBuffer/JSON decoding logic.
    const hasCustomHandler = typeof fetchArgs.responseHandler === "function";

    // Read the body exactly once, then decide how to interpret it.
    if (!hasCustomHandler)
      fetchArgs.responseHandler = async (response: Response) => {
        const contentType = response.headers.get("Content-Type") ?? "";
        const writtenWith = responseSchema(response);
        noteServerSchema(baseUrl, writtenWith);

        // Error responses always come as JSON (middleware passes them through).
        if (!response.ok) {
          try {
            const errorJson = await response.json();
            if (intercepting) {
              capturedResponseBody = errorJson;
              capturedError = `${response.status} ${response.statusText}`;
            }
            return errorJson;
          } catch {
            const fallback = { error: response.statusText, status_code: response.status };
            if (intercepting) {
              capturedResponseBody = fallback;
              capturedError = `${response.status} ${response.statusText}`;
            }
            return fallback;
          }
        }

        // Read body once into ArrayBuffer — prevents "body stream already read".
        const buffer = await response.arrayBuffer();
        const bytes = new Uint8Array(buffer);

        // If the server responded with FlatBuffer binary, decode it.
        if (contentType.includes("application/flatbuffers")) {
          capturedIsFlatBuffer = true;
          const envelope = extractEnvelopeWithHash(bytes);
          if (!envelope) {
            const head = Array.from(bytes.slice(0, 8))
              .map((b) => b.toString(16).padStart(2, "0"))
              .join(" ");
            const msg = `[FlatBuffer] Invalid LN2E envelope on ${method} ${fetchArgs.url} — ${bytes.length} bytes received, first 8 bytes: ${head}`;
            logError(msg);
            if (intercepting) capturedError = msg;
            throw new Error(msg);
          }
          capturedSchemaHash = envelope.schemaHash;
          const handler = getSchemaHandler(envelope.schemaHash);
          if (!handler) {
            const msg = `[FlatBuffer] No schema handler registered for hash ${envelope.schemaHash} on ${method} ${fetchArgs.url} — payload ${envelope.payload.length} bytes`;
            logError(msg);
            if (intercepting) capturedError = msg;
            throw new Error(msg);
          }
          if (intercepting) capturedTypeName = handler.name;
          schemaRefusal = await responseSchemaRefusal(
            baseUrl,
            writtenWith,
            handler.typeName,
            authorization,
          );
          if (schemaRefusal !== null) {
            const msg = `[FlatBuffer] ${schemaRefusal} (${method} ${fetchArgs.url})`;
            logError(msg);
            if (intercepting) capturedError = msg;
            return null;
          }
          // A decode failure is rethrown as an Error carrying its [FlatBuffer]
          // message, whatever the decoder threw (the original is its `cause`).
          // fetchBaseQuery reports it as a PARSING_ERROR holding String() of
          // it, and the retry rule (redux/retryConditions.ts) reads a TypeError
          // there as a body read cut short, which a resend can fix. A decoder's
          // own TypeError on a body that did arrive must not look like one.
          try {
            handler.verify(envelope.payload);
          } catch (verifyError) {
            const msg = `[FlatBuffer] verify() failed for ${handler.name} on ${method} ${
              fetchArgs.url
            }: ${String(verifyError)}`;
            logError(msg, verifyError);
            if (intercepting) capturedError = msg;
            throw new Error(msg, { cause: verifyError });
          }
          let unpacked: unknown;
          try {
            unpacked = handler.unpack(envelope.payload);
          } catch (unpackError) {
            const msg = `[FlatBuffer] unpack() failed for ${handler.name} on ${method} ${
              fetchArgs.url
            }: ${String(unpackError)}`;
            logError(msg, unpackError);
            if (intercepting) capturedError = msg;
            throw new Error(msg, { cause: unpackError });
          }
          if (intercepting) capturedResponseBody = unpacked;
          return unpacked;
        }

        // Non-FlatBuffer response — fall back to JSON parsing.
        // Expected during migration while some endpoints still return JSON.
        const text = new TextDecoder().decode(bytes);
        // 204 No Content and other empty-body 2xx responses (e.g. DELETE
        // /keys/:id) reach here with zero bytes. JSON.parse("") would throw
        // into the fallback branch and surface as a spurious error. Treat
        // empty bodies on successful responses as null data.
        if (text === "") {
          if (intercepting) capturedResponseBody = null;
          return null;
        }
        try {
          const parsed = JSON.parse(text);
          if (intercepting) capturedResponseBody = parsed;
          return parsed;
        } catch {
          const fallback = { error: "Failed to parse response", raw: text };
          if (intercepting) {
            capturedResponseBody = fallback;
            capturedError = "Failed to parse response";
          }
          return fallback;
        }
      };

    const result = await jsonBaseQuery(fetchArgs, api, extraOptions);

    // Emit endpoint event to interceptor subscribers.
    if (intercepting) {
      const duration = performance.now() - startTime;
      let status = 0;
      if (result.error && typeof result.error === "object" && "status" in result.error) {
        status = (result.error as { status: number }).status;
      } else if (result.data !== undefined) {
        status = 200;
      }
      emitEndpointEvent({
        id: nextEndpointEventId(),
        timestamp: Date.now(),
        method,
        url: fetchArgs.url,
        status,
        duration: Math.round(duration),
        requestBody: originalBody instanceof FormData ? "[FormData]" : (originalBody ?? null),
        responseBody: capturedResponseBody ?? result.data ?? null,
        schemaHash: capturedSchemaHash,
        typeName: capturedTypeName,
        error: capturedError,
        isFlatBuffer: capturedIsFlatBuffer,
      });
    }

    if (schemaRefusal !== null) {
      return {
        error: {
          status: "CUSTOM_ERROR",
          error: schemaRefusal,
          data: { message: schemaRefusal, reason: SCHEMA_REFUSAL },
        },
        meta: result.meta,
      };
    }
    return result;
  };
}

export { createFlatBufferBaseQuery };
export default createFlatBufferBaseQuery;
